import { Injectable, Injector, inject, runInInjectionContext } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {
  collection,
  collectionData,
  collectionGroup,
  doc,
  docData,
  Firestore, getDoc,
  query,
  where,
  getAggregateFromServer,
  sum
} from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { tap } from "rxjs/operators";
import { Timestamp } from 'firebase/firestore'
import { from, map } from 'rxjs';
import moment from "moment";

/**
 * Service providing a centralized data-access layer for Google Cloud Firestore and Firebase Cloud Functions.
 * Handles client documents, location data, real-time worker/device streams, registrations, session aggregation,
 * and authentication tokens.
 */
@Injectable({
  providedIn: 'root',
})
export class FirestoreService {
  private clientId: string;
  private loggedInUserUid: string;

  public http = inject(HttpClient);
  private firestore = inject(Firestore);
  private functions = inject(Functions);
  private injector = inject(Injector);

  constructor() {}

  /**
   * Sets the active client ID for scoped Firestore queries.
   * @param clientId - The client document ID string.
   */
  setClientId(clientId: string): void {
    this.clientId = clientId;
  }

  /**
   * Gets the active client ID.
   * @returns Current client ID string.
   */
  getClientId(): string {
    return this.clientId;
  }

  /**
   * Sets the logged-in user's UID.
   * @param uid - The Firebase Auth UID.
   */
  setLoggedInUserUid(uid: string): void {
    this.loggedInUserUid = uid;
  }

  /**
   * Gets the active user's UID.
   * @returns Active UID string.
   */
  getLoggedInUserUid(): string {
    return this.loggedInUserUid;
  }

  /**
   * Retrieves an Observable stream for a client document by ID.
   * @param clientId - The document ID of the client.
   * @returns Observable emitting the client document data with an added `id` field.
   */
  getClientById(clientId: string): Observable<any> {
    return runInInjectionContext(this.injector, () => {
      const clientDoc = doc(this.firestore, `clients/${clientId}`);
      return docData(clientDoc, { idField: 'id' });
    });
  }

  /**
   * Fetches a single location document for a specified client.
   * @param locationId - The document ID of the location.
   * @param clientId - The parent client document ID.
   * @returns Promise resolving to the Firestore DocumentSnapshot.
   */
  getLocationByIdForClientId(locationId: string, clientId: string): Promise<any> {
    return runInInjectionContext(this.injector, () => {
      const locDoc = doc(this.firestore, `clients/${clientId}/locations/${locationId}`);
      return getDoc(locDoc);
    });
  }

  /**
   * Streams all active devices for the current client, optionally filtered by location.
   * @param locationId - Optional location ID to filter devices.
   * @returns Observable emitting an array of device documents.
   */
  getAllDevicesForLocationId(locationId: string = null): Observable<any> {
    return runInInjectionContext(this.injector, () => {
      const devicesRef = collection(this.firestore, 'devices');
      let devicesQuery;
      if (locationId) {
        devicesQuery = query(
          devicesRef,
          where('clientId', '==', this.clientId),
          where('locationId', '==', locationId),
          where('isArchived', '==', false)
        );
      } else {
        devicesQuery = query(
          devicesRef,
          where('clientId', '==', this.clientId),
          where('isArchived', '==', false)
        );
      }
      return collectionData(devicesQuery, { idField: 'id' });
    });
  }

  /**
   * Streams all active workers for the current client, optionally filtered by location.
   * @param locationId - Optional location ID to filter workers.
   * @returns Observable emitting an array of worker documents.
   */
  getAllWorkersForLocationId(locationId: string = null): Observable<any> {
    return runInInjectionContext(this.injector, () => {
      const workersRef = collection(this.firestore, `clients/${this.clientId}/workers`);
      let q;

      if (!locationId) {
        q = query(
          workersRef,
          where('isArchived', '==', false)
        );

      } else {
        q = query(
          workersRef,
          where('locationIds', 'array-contains', locationId),
          where('isArchived', '==', false)
        );

      }
      return collectionData(q, { idField: 'id' });
    });
  }

  /**
   * Fetches translation dictionary JSON document for a specified language code.
   * @param languageCode - ISO language code (e.g. 'en', 'nl').
   * @returns Observable emitting language translation key-value map.
   */
  getLanguageJSON(languageCode: string): Observable<any> {
    return runInInjectionContext(this.injector, () => {
      const langDoc = doc(this.firestore, `languages/${languageCode}`);
      return docData(langDoc, { idField: 'id' });
    });
  }

  /**
   * Streams all active registrations created today for the current client, optionally filtered by location.
   * @param locationId - Optional location ID filter.
   * @returns Observable emitting an array of registration documents.
   */
  getAllRegistrationsForLocationId(locationId: string = null): Observable<any> {
    const fromTimestamp = Timestamp.fromDate(moment().startOf('day').toDate());
    const toTimestamp = Timestamp.fromDate(moment().endOf('day').toDate());
    return runInInjectionContext(this.injector, () => {
      let q;
      if (locationId) {
        q = query(
          collectionGroup(this.firestore, 'registrations'),
          where('startTimestamp', '>', fromTimestamp),
          where('startTimestamp', '<=', toTimestamp),
          where('clientId', '==', this.clientId),
          where('locationId', '==', locationId),
          where('isArchived', '==', false)
        );
      } else {
        q = query(
          collectionGroup(this.firestore, 'registrations'),
          where('startTimestamp', '>', fromTimestamp),
          where('startTimestamp', '<=', toTimestamp),
          where('clientId', '==', this.clientId),
          where('isArchived', '==', false)
        );
      }
      return collectionData(q, { idField: 'id' });
    });
  }

  /**
   * Streams real-time sum of total session counts created today across the client/location.
   * @param locationId - Optional location ID filter.
   * @returns Observable emitting the total aggregated session count number.
   */
  getSessionCountForLocationId(locationId: string = null): Observable<number> {
    const fromTimestamp = Timestamp.fromDate(moment().startOf('day').toDate());
    const toTimestamp = Timestamp.fromDate(moment().endOf('day').toDate());

    return runInInjectionContext(this.injector, () => {
      let q = query(
        collectionGroup(this.firestore, 'sessions'),
        where('startTimestamp', '>', fromTimestamp),
        where('startTimestamp', '<=', toTimestamp),
        where('clientId', '==', this.clientId),
        where('isArchived', '==', false)
      );

      // Keep it DRY: only add locationId if it exists
      if (locationId) {
        q = query(q, where('locationId', '==', locationId));
      }

      // collectionData listens for live updates.
      // Every time a doc changes, it recalculates the sum.
      return collectionData(q).pipe(
        map((sessions: any[]) =>
          sessions.reduce((acc, curr) => acc + (curr.countCorrectedAtCreation || 0), 0)
        )
      );
    });
  }

  /**
   * Invokes Cloud Function `getCastAuth` to generate a custom token for the casting key.
   * @param castingKey - Casting authentication key string.
   * @returns Promise resolving to the authentication response containing custom token.
   */
  getSignInToken(castingKey: string): Promise<any> {
    const callable = httpsCallable(this.functions, 'getCastAuth');
    return callable({
      castingKey
    });
  }

}

